import { useEffect, useRef, useState, type ReactNode } from "react";
import { Heat } from "../../ui/Heat";
import { Star } from "../../ui/Star";
import { LockIcon, PortfolioIcon } from "../../ui/icons";
import { copyToClipboard } from "../../lib/clipboard";
import { fmtDate, fmtNum, monthsUntil } from "../../lib/format";
import type { ScreenedBond } from "./useScreener";

type SortKey =
  | "secid"
  | "last_price"
  | "gross_ytm"
  | "net_ytm"
  | "yield_horizon"
  | "months"
  | "target_clean_price";

const COLS: { key: SortKey | null; label: string; num?: boolean }[] = [
  { key: null, label: "" },
  { key: "secid", label: "SECID" },
  { key: null, label: "Эмитент / Название" },
  { key: "last_price", label: "Price", num: true },
  { key: "gross_ytm", label: "Gross %", num: true },
  { key: "net_ytm", label: "Net %", num: true },
  { key: "yield_horizon", label: "Horizon" },
  { key: "months", label: "Срок, мес", num: true },
  { key: "target_clean_price", label: "Target", num: true },
  { key: null, label: "V" },
  { key: null, label: "" },
];

function valueFor(b: ScreenedBond, key: SortKey): number | string | null {
  switch (key) {
    case "secid":
      return b.secid;
    case "last_price":
      return b.last_price;
    case "gross_ytm":
      return b.gross_ytm;
    case "net_ytm":
      return b.net_ytm;
    case "yield_horizon":
      return b.yield_horizon ?? "";
    case "months":
      return monthsUntil(b.yield_horizon);
    case "target_clean_price":
      return b.target_clean_price;
  }
}

function compare(a: ScreenedBond, b: ScreenedBond, key: SortKey): number {
  const av = valueFor(a, key);
  const bv = valueFor(b, key);
  if (av == null && bv == null) return 0;
  if (av == null) return 1;
  if (bv == null) return -1;
  if (typeof av === "string" && typeof bv === "string") return av.localeCompare(bv);
  return (av as number) - (bv as number);
}

export function ScreenerTable({
  rows,
  target,
  favorites,
  hidden,
  inPortfolio,
  onToggleFav,
  onToggleHidden,
  onAddToPortfolio,
  onAddAlert,
  onPick,
}: {
  rows: ScreenedBond[];
  target: number;
  favorites: Set<string>;
  hidden: Set<string>;
  inPortfolio: Set<string>;
  onToggleFav: (secid: string) => void;
  onToggleHidden: (secid: string) => void;
  onAddToPortfolio: (secid: string) => void;
  onAddAlert: (secid: string) => void;
  onPick: (secid: string) => void;
}) {
  const [sortKey, setSortKey] = useState<SortKey>("net_ytm");
  const [sortDesc, setSortDesc] = useState(true);
  const [menuFor, setMenuFor] = useState<string | null>(null);

  const sorted = [...rows].sort((a, b) => {
    const cmp = compare(a, b, sortKey);
    return sortDesc ? -cmp : cmp;
  });

  const onHeader = (key: SortKey | null) => {
    if (key == null) return;
    if (key === sortKey) setSortDesc((d) => !d);
    else {
      setSortKey(key);
      setSortDesc(true);
    }
  };

  return (
    <table className="sk screener-tbl">
      <thead>
        <tr>
          {COLS.map((c, i) => (
            <Th
              key={i}
              num={c.num}
              sortable={c.key != null}
              active={c.key === sortKey}
              desc={sortDesc}
              onClick={() => onHeader(c.key)}
            >
              {c.label}
            </Th>
          ))}
        </tr>
      </thead>
      <tbody>
        {sorted.map((b) => (
          <Row
            key={b.secid}
            row={b}
            target={target}
            isFav={favorites.has(b.secid)}
            isHidden={hidden.has(b.secid)}
            isInPortfolio={inPortfolio.has(b.secid)}
            menuOpen={menuFor === b.secid}
            onOpenMenu={() => setMenuFor(b.secid)}
            onCloseMenu={() => setMenuFor(null)}
            onToggleFav={() => onToggleFav(b.secid)}
            onToggleHidden={() => onToggleHidden(b.secid)}
            onAddToPortfolio={() => onAddToPortfolio(b.secid)}
            onAddAlert={() => onAddAlert(b.secid)}
            onPick={() => onPick(b.secid)}
          />
        ))}
      </tbody>
    </table>
  );
}

function Th({
  children,
  num,
  sortable,
  active,
  desc,
  onClick,
}: {
  children: ReactNode;
  num?: boolean;
  sortable?: boolean;
  active?: boolean;
  desc?: boolean;
  onClick?: () => void;
}) {
  const cls = [
    num ? "num" : "",
    sortable ? "sortable" : "",
    active ? (desc ? "sort-desc" : "sort-asc") : "",
  ]
    .filter(Boolean)
    .join(" ");
  return (
    <th
      className={cls || undefined}
      onClick={sortable ? onClick : undefined}
      style={sortable ? { cursor: "pointer", userSelect: "none" } : undefined}
    >
      {children}
    </th>
  );
}

function Row({
  row,
  target,
  isFav,
  isHidden,
  isInPortfolio,
  menuOpen,
  onOpenMenu,
  onCloseMenu,
  onToggleFav,
  onToggleHidden,
  onAddToPortfolio,
  onAddAlert,
  onPick,
}: {
  row: ScreenedBond;
  target: number;
  isFav: boolean;
  isHidden: boolean;
  isInPortfolio: boolean;
  menuOpen: boolean;
  onOpenMenu: () => void;
  onCloseMenu: () => void;
  onToggleFav: () => void;
  onToggleHidden: () => void;
  onAddToPortfolio: () => void;
  onAddAlert: () => void;
  onPick: () => void;
}) {
  const months = monthsUntil(row.yield_horizon);
  const isOk = row.net_ytm >= target;
  return (
    <tr
      style={{ cursor: "pointer", opacity: isHidden ? 0.5 : 1 }}
      onClick={onPick}
    >
      <td onClick={(e) => e.stopPropagation()}>
        <span style={{ display: "inline-flex", alignItems: "center", gap: 4 }}>
          <Star on={isFav} onToggle={onToggleFav} />
          <LockBtn on={isHidden} onToggle={onToggleHidden} />
          {isInPortfolio && (
            <span
              className="port-marker"
              title="есть в портфеле"
              aria-label="есть в портфеле"
            >
              <PortfolioIcon />
            </span>
          )}
        </span>
      </td>
      <td className="mono" style={{ fontSize: 12 }}>{row.secid}</td>
      <td>
        <div>
          {row.secname ?? row.short_name}
          {row.list_level === 3 && <span className="tag-l3">L3</span>}
        </div>
        <div className="soft" style={{ fontSize: 12 }}>{row.short_name}</div>
      </td>
      <td className="num">{fmtNum(row.last_price)} ₽</td>
      <td className={`num ${isOk ? "ok" : ""}`}>{fmtNum(row.gross_ytm * 100)}</td>
      <td className="num"><Heat value={row.net_ytm} /></td>
      <td className="mono" style={{ fontSize: 12 }}>{fmtDate(row.yield_horizon)}</td>
      <td className="num">{months == null ? "—" : months.toFixed(1)}</td>
      <td className="num">
        {row.target_clean_price == null ? "—" : `${fmtNum(row.target_clean_price)} ₽`}
      </td>
      <td>
        <span style={{ display: "inline-flex", gap: 4 }}>
          {isOk && (
            <span className="ok mono" style={{ fontSize: 12 }}>OK</span>
          )}
          {row.is_floater && (
            <span
              className="chip"
              style={{ borderColor: "var(--accent)", color: "var(--accent)" }}
              title="плавающий купон"
            >
              F
            </span>
          )}
        </span>
      </td>
      <td onClick={(e) => e.stopPropagation()} style={{ position: "relative" }}>
        <button
          type="button"
          className="btn sm"
          onClick={() => (menuOpen ? onCloseMenu() : onOpenMenu())}
          aria-label="действия"
        >
          ⋯
        </button>
        {menuOpen && (
          <RowMenu
            secname={row.secname ?? row.short_name}
            onClose={onCloseMenu}
            onAddToPortfolio={onAddToPortfolio}
            onAddAlert={onAddAlert}
          />
        )}
      </td>
    </tr>
  );
}

function LockBtn({ on, onToggle }: { on: boolean; onToggle: () => void }) {
  return (
    <button
      type="button"
      className={`lock-btn${on ? " on" : ""}`}
      onClick={onToggle}
      aria-pressed={on}
      title={on ? "недоступно — клик чтобы вернуть" : "пометить недоступной"}
    >
      <LockIcon hidden={on} />
    </button>
  );
}

function RowMenu({
  secname,
  onClose,
  onAddToPortfolio,
  onAddAlert,
}: {
  secname: string;
  onClose: () => void;
  onAddToPortfolio: () => void;
  onAddAlert: () => void;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const [copied, setCopied] = useState(false);
  useEffect(() => {
    const onDoc = (e: MouseEvent) => {
      if (ref.current && !ref.current.contains(e.target as Node)) onClose();
    };
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [onClose]);
  return (
    <div ref={ref} className="row-menu">
      <button
        type="button"
        className="row-menu-item"
        onClick={() => {
          onAddToPortfolio();
          onClose();
        }}
      >
        <span className="row-menu-ic">+</span>В портфель
      </button>
      <button
        type="button"
        className="row-menu-item"
        onClick={async () => {
          const ok = await copyToClipboard(secname);
          if (ok) {
            setCopied(true);
            setTimeout(onClose, 700);
          } else {
            onClose();
          }
        }}
      >
        <span className="row-menu-ic">{copied ? "✓" : "⊕"}</span>
        {copied ? "Скопировано" : "Скопировать эмитента"}
      </button>
      <button
        type="button"
        className="row-menu-item"
        onClick={() => {
          onAddAlert();
          onClose();
        }}
      >
        <span className="row-menu-ic">⊪</span>Добавить алерт
      </button>
    </div>
  );
}
